/**
 * "Vous aimerez aussi" — sourced from our own search engine.
 *
 * Shopify's related-products algorithm leans on order history this store does
 * not have yet, so it padded thin categories with whatever was nearby: a metal
 * cash box was shown next to a girls' wallet, a digital watch and a mini car.
 * The engine ranks on the catalogue itself (product line from the title, then
 * category, brand within that category, and a comparable price).
 *
 * Progressive enhancement: Shopify's own list is already rendered when this
 * runs. We replace it only when the engine returns a confident set, so any
 * failure leaves the page exactly as it was.
 *
 * Card markup mirrors snippets/gdj-card-inner.liquid — same classes and data
 * attributes, so favourites, "seen", in-cart badges and quick-add keep working.
 */
(function () {
  const host = document.querySelector("product-recommendations[data-gdj-api]");
  if (!host) return;

  const API = (host.dataset.gdjApi || "").replace(/\/$/, "");
  const SHOP = host.dataset.shop || "";
  const PRODUCT_ID = (host.dataset.productId || "").replace(/^.*\//, "");
  const CURRENCY = host.dataset.currency || "CAD";
  const LOCALE = document.documentElement.lang || "fr-CA";
  const IS_EN = LOCALE.toLowerCase().startsWith("en");
  const T = {
    sale: IS_EN ? "Sale" : "Solde",
    preorder: IS_EN ? "Pre-order" : "Précommande",
    sku: IS_EN ? "Ref." : "Réf.",
    quickAdd: IS_EN ? "Quick add" : "Ajout rapide",
    seen: IS_EN ? "Seen" : "Vu",
    fav: IS_EN ? "Favorite" : "Favori",
    wishlist: IS_EN ? "Favorites" : "Favoris",
  };
  // Fewer than this and the row looks thinner than what Shopify already drew.
  const MIN_PRODUCTS = 3;
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";

  const esc = (v) => String(v == null ? "" : v).replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

  const money = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return "";
    try {
      return new Intl.NumberFormat(LOCALE, { style: "currency", currency: CURRENCY }).format(n);
    } catch (_) { return n.toFixed(2) + " $"; }
  };

  const heart = '<svg class="gdj-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/></svg>';
  const cartSvg = '<svg class="gdj-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="8" cy="21" r="1"/><circle cx="19" cy="21" r="1"/><path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"/></svg>';

  function cardHtml(p) {
    const img = (p.images && p.images[0] && (p.images[0].url || p.images[0]))
      || (p.variants && p.variants[0] && p.variants[0].image) || "";
    const onSale = p.special_price != null && p.regular_price != null
      && Number(p.special_price) < Number(p.regular_price);
    const shown = p.special_price != null ? p.special_price : p.regular_price;
    const url = root + "products/" + esc(p.handle);
    const pid = String(p.id).replace(/^.*\//, "");
    const sku = (p.variants && p.variants[0] && p.variants[0].sku) ? String(p.variants[0].sku).trim() : "";
    const price = onSale
      ? '<span class="gdj-card__price-sale">' + money(p.special_price) + '</span> <s class="gdj-card__price-was">' + money(p.regular_price) + "</s>"
      : "<span>" + money(shown) + "</span>";
    return '<div class="resource-list__item">' +
      '<article class="gdj-card" data-product-id="' + esc(pid) + '" data-product-handle="' + esc(p.handle) + '">' +
      '<div class="gdj-card__media-wrap"><div class="gdj-card__corner">' +
      (p.display_label ? '<span class="gdj-card__badge">' + esc(p.display_label) + "</span>" : "") +
      (p.is_preorder ? '<span class="gdj-card__badge gdj-card__badge--preorder">' + esc(T.preorder) + "</span>" : "") +
      (onSale ? '<span class="gdj-card__badge">' + esc(T.sale) + "</span>" : "") +
      '<span class="gdj-card__pill gdj-card__pill--seen" data-gdj-seenpill hidden>' + esc(T.seen) + "</span>" +
      '<span class="gdj-card__pill gdj-card__pill--fav" data-gdj-favpill hidden>' + heart + esc(T.fav) + "</span></div>" +
      '<span class="gdj-card__incart" data-gdj-incart hidden><span class="gdj-card__incart-x">&times;</span><b data-gdj-incart-qty>1</b></span>' +
      '<a href="' + url + '" class="gdj-card__media" aria-label="' + esc(p.title) + '">' +
      (img ? '<img class="gdj-card__img" src="' + esc(img) + '" alt="" loading="lazy">' : "") + "</a>" +
      '<button type="button" class="gdj-card__fav" data-fav-btn="' + esc(pid) + '" data-fav-handle="' + esc(p.handle) + '" aria-label="' + esc(T.wishlist) + '">' + heart + "</button></div>" +
      '<div class="gdj-card__body">' +
      '<a href="' + url + '" class="gdj-card__title">' + esc(p.title) + "</a>" +
      (sku ? '<div class="gdj-card__sku">' + esc(T.sku) + " " + esc(sku) + "</div>" : "") +
      '<div class="gdj-card__price">' + price + "</div>" +
      '<button type="button" class="gdj-card__quick" data-gdj-quick-add="' + esc(p.handle) + '">' + cartSvg + "<span>" + esc(p.is_preorder ? T.preorder : T.quickAdd) + "</span></button>" +
      "</div></article></div>";
  }

  async function run() {
    if (!API || !SHOP || !PRODUCT_ID) return;
    let products = [];
    try {
      const res = await fetch(
        `${API}/api/v1/products/related?shop=${encodeURIComponent(SHOP)}&product_id=${encodeURIComponent(PRODUCT_ID)}&limit=8`
      );
      if (!res.ok) return;
      products = (await res.json()).products || [];
    } catch (_) { return; }
    if (products.length < MIN_PRODUCTS) return;

    // Every layout this section can render (grid, carousel, mobile carousel)
    // hangs its items off .resource-list, so fill each one it built.
    const lists = host.querySelectorAll(".resource-list");
    if (!lists.length) return;
    const slots = lists[0].querySelectorAll(".resource-list__item").length || products.length;
    const html = products.slice(0, Math.max(MIN_PRODUCTS, slots)).map(cardHtml).join("");
    lists.forEach((list) => { list.innerHTML = html; });

    // Let the badge/favourite scripts decorate the cards we just injected.
    document.dispatchEvent(new CustomEvent("gdj:cards-rendered", { detail: { root: host } }));
  }

  // The section fetches its own markup first; wait a beat so we replace the
  // finished list rather than race it.
  if (document.readyState === "complete") setTimeout(run, 400);
  else window.addEventListener("load", () => setTimeout(run, 400));
})();
